/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Stack } from '@mui/material';
import React from 'react';
import { FilterChip } from '@opengeoweb/shared';
import type { DistrictsType } from '../types';
import { useFilterChipSelect } from '../useFilterChipSelect';

export interface AreaListProps {
  areas: DistrictsType[];
  selected: string[];
  disabled: string[];
  isReadOnly?: boolean;
  onToggleSelectArea?: (id: string) => void;
  onSelectMultipleAreas: (ids: string[]) => void;
}

export const AreaList = React.memo(
  ({
    areas,
    selected,
    disabled,
    isReadOnly,
    onToggleSelectArea,
    onSelectMultipleAreas,
  }: AreaListProps) => {
    const { handleChipClick } = useFilterChipSelect({
      selectedIds: selected,
      assignedIds: areas.map((area) => area.districtName),
      onSelectMultiple: onSelectMultipleAreas,
      onToggleSelect: onToggleSelectArea,
    });

    return (
      <Stack
        data-testid="arealist"
        direction="row"
        spacing={1}
        style={{
          pointerEvents: !isReadOnly ? 'auto' : 'none',
        }}
        sx={{
          display: 'block',
          '.MuiChip-root': {
            margin: 0,
            marginRight: 1,
            marginBottom: 1,
          },
        }}
      >
        {areas.map((area) => {
          const isDisabled = disabled.includes(area.districtName);
          const isSelected =
            !isDisabled && selected.includes(area.districtName);

          return (
            <FilterChip
              id={area.districtName}
              isDisabled={isDisabled}
              isSelected={isSelected}
              key={area.districtName}
              label={area.districtName}
              onClick={(_ref, event) => {
                if (!isReadOnly) {
                  handleChipClick?.(area.districtName, event);
                }
              }}
              size="medium"
            />
          );
        })}
      </Stack>
    );
  },
);
